import { useState, useEffect } from 'react';
import { Users, Mail, Check, X, Clock, Search, Filter, UserPlus } from 'lucide-react';
import { supabase } from '../hooks/useAuth';
import Card, { CardHeader } from '../components/common/Card';
import Button from '../components/common/Button';

interface BetaSignup {
  id: string;
  email: string;
  name: string;
  artist_type: string;
  status: 'pending' | 'approved' | 'rejected';
  source: string;
  notes: string | null;
  created_at: string;
  approved_at: string | null;
}

export default function BetaUsers() {
  const [signups, setSignups] = useState<BetaSignup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSignups();
  }, []);

  const fetchSignups = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('beta_signups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching signups:', error);
    } else {
      setSignups(data || []);
    }
    setIsLoading(false);
  };

  const updateStatus = async (id: string, status: 'approved' | 'rejected') => {
    const updateData: Record<string, unknown> = { status };
    if (status === 'approved') {
      updateData.approved_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('beta_signups')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Error updating status:', error);
    } else {
      setSignups(signups.map(s =>
        s.id === id ? { ...s, status, approved_at: status === 'approved' ? new Date().toISOString() : s.approved_at } : s
      ));
    }
  };

  const filteredSignups = signups.filter(s => {
    const matchesFilter = filter === 'all' || s.status === filter;
    const matchesSearch = search === '' ||
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      s.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: signups.length,
    pending: signups.filter(s => s.status === 'pending').length,
    approved: signups.filter(s => s.status === 'approved').length,
    rejected: signups.filter(s => s.status === 'rejected').length,
  };

  const statusColors = {
    pending: 'bg-yellow-900/50 text-yellow-400',
    approved: 'bg-green-900/50 text-green-400',
    rejected: 'bg-red-900/50 text-red-400',
  };

  const artistTypeLabels: Record<string, string> = {
    musician: 'Musician / Producer',
    band: 'Band',
    dj: 'DJ',
    label: 'Record Label',
    manager: 'Artist Manager',
    other: 'Other',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Beta Users</h1>
        <p className="text-zinc-400 mt-1">Manage beta signup requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-400/10">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Total</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-400/10">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Pending</p>
              <p className="text-xl font-bold">{stats.pending}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-400/10">
              <Check className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Approved</p>
              <p className="text-xl font-bold">{stats.approved}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-400/10">
              <X className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Rejected</p>
              <p className="text-xl font-bold">{stats.rejected}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-jelly-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-zinc-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-jelly-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Signups List */}
      <Card>
        <CardHeader
          title="Signups"
          description={`${filteredSignups.length} ${filter === 'all' ? 'total' : filter} signups`}
        />

        {isLoading ? (
          <div className="text-center py-8 text-zinc-500">Loading...</div>
        ) : filteredSignups.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No signups found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-zinc-500 border-b border-zinc-800">
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium">Type</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredSignups.map((signup) => (
                  <tr key={signup.id} className="hover:bg-zinc-800/50">
                    <td className="py-4">
                      <div>
                        <p className="font-medium">{signup.name}</p>
                        <p className="text-sm text-zinc-400">{signup.email}</p>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="text-sm text-zinc-400">
                        {artistTypeLabels[signup.artist_type] || signup.artist_type}
                      </span>
                    </td>
                    <td className="py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[signup.status]}`}>
                        {signup.status}
                      </span>
                    </td>
                    <td className="py-4 text-sm text-zinc-400">
                      {new Date(signup.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center justify-end gap-2">
                        {signup.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus(signup.id, 'approved')}
                              className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus(signup.id, 'rejected')}
                              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <a
                          href={`mailto:${signup.email}`}
                          className="p-2 text-zinc-400 hover:text-white transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
